"use client";

import { Section } from "../types";
import { useFormState } from "./useFormState";
import { useTreeData } from "./useTreeData";
import { useParentComponent } from "./useParentComponent";
import { useTreeItemActions } from "./useTreeItemActions";

export interface UseForm2Props {
  selectedSection: Section;
  pageId?: string;
  pageComponentId?: string;
  variantDetails?: any; // Passed from parent to avoid circular dependency
}

export const useForm2 = ({ selectedSection, pageId, pageComponentId, variantDetails }: UseForm2Props) => {
  // Core form state and config
  const {
    formState,
    setFormState,
    dispatch,
    currentVariantDetails,
    currentUser,
    reduxState,
    componentFieldsConfig,
    parentComponentFieldsConfig,
    treeConfig,
    fieldsConfig,
    fetchMethod,
    deleteMethod,
    parentComponentGetMethod,
    parentComponentCreateMethod,
    parentComponentUpdateMethod,
  } = useFormState({ selectedSection, pageId, pageComponentId, variantDetails });

  // Tree data management
  const { fetchTreeItems } = useTreeData({
    selectedSection,
    formState,
    setFormState,
    treeConfig,
    componentFieldsConfig,
    parentComponentFieldsConfig,
    fetchMethod,
  });

  // Parent component management
  const { handleParentComponentFieldsUpdate, handleParentComponentSave } = useParentComponent({
    selectedSection,
    formState,
    setFormState,
    treeConfig,
    parentComponentFieldsConfig,
    parentComponentGetMethod,
    parentComponentCreateMethod,
    parentComponentUpdateMethod,
    fetchMethod,
    currentUserId: currentUser?.id,
    pageId,
    pageComponentId,
  });

  // Tree item actions
  const {
    handleComponentFieldsUpdate,
    handleSave,
    handleEdit,
    handleDelete,
    handleNewItem,
    toggleExpand,
  } = useTreeItemActions({
    selectedSection,
    formState,
    setFormState,
    treeConfig,
    componentFieldsConfig,
    fieldsConfig,
    currentVariantDetails,
    fetchMethod,
    deleteMethod,
    currentUserId: currentUser?.id,
    pageId,
    pageComponentId,
    reduxState,
    dispatch,
    fetchTreeItems,
  });

  return {
    formState,
    currentVariantDetails,
    treeConfig,
    componentFieldsConfig,
    parentComponentFieldsConfig,
    fieldsConfig,
    handleParentComponentFieldsUpdate,
    handleComponentFieldsUpdate,
    handleParentComponentSave,
    handleSave,
    handleEdit,
    handleDelete,
    handleNewItem,
    toggleExpand,
  };
};

// Re-export individual hooks for direct use
export { useFormState } from "./useFormState";
export { useTreeData } from "./useTreeData";
export { useParentComponent } from "./useParentComponent";
export { useTreeItemActions } from "./useTreeItemActions";
export * from "./types";

